/**
 * Page runtime context (ADR-0010 P7b3 · S5) — page-scoped reactive state so a
 * button's actions (set-var / refresh / navigate / toast) and a data_list's
 * row actions (open / edit in a drawer, delete) update the live page.
 * Provided by the portal page view around the renderer; absent in the admin
 * builder preview (where buttons/forms/records render inert).
 *
 * State is plain React `useState` (page-scoped), not a global store.
 */
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  type PortalPageResult,
  type PortalReportParam,
  type PortalReportParamOption,
  type PortalRole,
  type PortalRowsPage,
  portalApi,
} from "@/lib/api-portal";
import type { ActionExecMode } from "@/lib/pageActions";
import type { Scope } from "@/lib/pageExpr";
import { allDepsEmpty, computeRuntimeParams, isReactive } from "@/lib/pageResourceParams";
import type { PortalUser } from "@/lib/portalAuth";
import {
  clearStore,
  removeValue,
  seedGlobals,
  storeSnapshot,
  storeValue,
  subscribeStore,
} from "@/lib/portalStore";
import { createReactiveEngine, type ReactiveEngine } from "@/lib/reactive/engine";
import { pageInitialState, pageToCells } from "@/lib/reactive/pageCells";

import { PortalOverlay } from "./PortalOverlay";

/** Hard cap on nested overlays — opening past this is a silent no-op (a runaway
 *  `open_overlay` chain can't stack dialogs to infinity). */
const MAX_OVERLAY_DEPTH = 3;

/** One entry on the runtime's overlay stack (ADR-0023). */
interface OverlayEntry {
  /** Stable key for React + targeted close. */
  id: number;
  pageSlug: string;
  mode: "modal" | "drawer";
  /** Seed vars merged into the overlay page's `$page` (record drawer). */
  vars: Record<string, unknown>;
  /** Read-only `$input` namespace for the overlay page (open_overlay context). */
  input: Record<string, unknown>;
  /** The opener's `on_close` runner — invoked with the returned `$result` when
   *  this overlay closes (undefined for the plain record drawer). */
  onCloseChain?: (result: Record<string, unknown>) => void;
}

/** A dashboard-wide filter (3b): broadcast to EVERY page query (the server
 *  drops it on tables that lack the column), so one control / chart click
 *  re-scopes the whole page — charts, KPIs and lists alike. */
export interface GlobalFilter {
  column: string;
  op: string;
  value: unknown;
  /** Human label for the active-filter chip (defaults to `column`). */
  label?: string;
}

export interface PageRuntime {
  appSlug: string;
  /** The current page's slug — the data_list uses it to reactively re-query a
   *  named page query (S16b). */
  pageSlug: string;
  user: PortalUser | null;
  role: PortalRole | null;
  queries: Record<string, PortalRowsPage>;
  /** Names of queries the reactive dep-graph OWNS (they declare `deps`, so their
   *  `{{ }}` params depend on the live `$page` — ADR-0022 Phase C). A `data_list`
   *  bound to one that ALSO enters server-side mode (S16b — searchable/sortable/
   *  paginated over the FULL dataset) MUST merge that query's EVALUATED `$page`
   *  filters (via `reactiveParams`) into its server fetch — otherwise the fetch is
   *  blind to `$page` (the backend also drops the unevaluated `{{ }}` template) →
   *  the "searchable master-detail shows nothing" bug. This is what makes the
   *  reactive filter + server-side search/paging scale to any row count. */
  reactiveQueryNames: Set<string>;
  /** The EVALUATED runtime params of a reactive query (its `{{ $page }}` filters/
   *  sort resolved against the live scope), or `null` for a non-reactive one. The
   *  server-side `data_list` (S16b) merges `.filters` into its fetch so the reactive
   *  `$page` filter still applies to search/sort/pagination over the full dataset. */
  reactiveParams: (name: string) => {
    filters?: { column: string; op: string; value: unknown }[];
    sort?: { column: string; descending: boolean }[];
    page_size?: number;
  } | null;
  vars: Record<string, unknown>;
  setVar: (name: string, value: unknown) => void;
  /** App-scoped global store (ADR-0043 — the `$global` namespace). Unlike `vars`
   *  (`$page`, per-page), it SURVIVES navigation — a wizard/cart spanning pages.
   *  Read in bindings as `{{ $global.<key> }}`. */
  store: Record<string, unknown>;
  /** Write a key into the global `$global` (the `global_value` action). */
  storeValue: (key: string, value: unknown) => void;
  /** Delete a key from the global `$global` (the `global_remove` action). */
  removeValue: (key: string) => void;
  /** Clear the whole app `$global` (the `global_clear` action). */
  clearStore: () => void;
  /** Active dashboard-wide filters (3b). A `data_list` ANDs these into its own
   *  reactive fetch; charts/KPIs read the re-fetched `queries`. */
  globalFilters: GlobalFilter[];
  /** Replace the active global filters and re-resolve every page query with
   *  them (drill-down: a chart click sets `[{column, op:"eq", value}]`; a
   *  filter control sets its own; `[]` clears). */
  setGlobalFilter: (filters: GlobalFilter[]) => void;
  refresh: () => void;
  /** Reactively re-resolve ONE named resource with current page-state
   *  (ADR-0022 Phase C). The caller evaluates the resource's `{{ }}` params
   *  against the live scope and passes the CONCRETE values; the result replaces
   *  `queries[name]` in place (the rest of the page is untouched). Used for
   *  `manual` resources (fetch on event) + the reactive dep-graph (refetch when
   *  a dep changes). */
  refreshResource: (
    name: string,
    params?: {
      filters?: { column: string; op: string; value: unknown }[];
      sort?: { column: string; descending: boolean }[];
      page_size?: number;
      inputs?: Record<string, unknown>;
      query?: Record<string, unknown>;
      url?: string;
      headers?: Record<string, unknown>;
      body?: unknown;
    },
    /** When true, a failed re-resolve PROPAGATES (rejects) instead of being
     *  swallowed — so the imperative `run_query` ACTION can be wrapped in a
     *  `try/catch` chain. The reactive auto-refetch path omits it (best-effort). */
    opts?: { throwOnError?: boolean },
  ) => Promise<void>;
  /** Imperatively (re)run ONE named page query — the `.run()` of ADR-0043 F3
   *  (the `run_query` action). Computes the query's `{{ }}` params from the
   *  LIVE scope and re-resolves it, so a `trigger: "manual"` query (which never
   *  auto-fetches) is finally reachable from a button. Subsumes the former
   *  `run_operation` action (ADR-0044 J3c): it AWAITS + PROPAGATES so an
   *  operation-query failure routes to the action chain's `catch`. Rejects for
   *  an unknown name only via the promise (the chain treats it as a no-op). */
  runQuery: (name: string) => Promise<void>;
  navigate: (to: string) => void;
  toast: (message: string) => void;
  /** Read-only `$input` namespace for THIS page when opened as an overlay
   *  (ADR-0023); `{}` for a normally-navigated page. */
  input: Record<string, unknown>;
  /** Open a target page in a slide-over drawer, seeding its `$page` vars
   *  (e.g. `{ row: <id> }`). On close the underlying page refreshes (S5). */
  openDrawer: (pageSlug: string, vars: Record<string, unknown>) => void;
  /** Open an authored overlay page (modal/drawer) on the overlay stack
   *  (ADR-0023), seeding its read-only `$input`. `onClose` runs the opener's
   *  `on_close` chain (with `$result`) once the overlay closes. */
  openOverlay: (opts: {
    page: string;
    mode: "modal" | "drawer";
    input: Record<string, unknown>;
    onClose?: (result: Record<string, unknown>) => void;
  }) => void;
  /** Close the overlay this runtime renders inside (undefined at top level).
   *  A create form calls it on success so Save → close → list refreshes. */
  closeDrawer?: () => void;
  /** Close the overlay this runtime renders inside, returning `result` to the
   *  opener's `on_close` (undefined at top level). The result-bearing twin of
   *  `closeDrawer`, used by the `close_overlay` action. */
  closeOverlay?: (result: Record<string, unknown>) => void;
  /** ADR-0045 — modal/drawer COMPONENTS (a `modal`/`drawer` node in the page
   *  tree, NOT a separate overlay page): which modal nodes are currently open,
   *  toggled by the `open_modal` / `close_modal` actions. The `modal`/`drawer`
   *  renderNode reads `isModalOpen(node.id)` to show or hide itself. */
  isModalOpen: (nodeId: string) => boolean;
  openModal: (nodeId: string) => void;
  closeModal: (nodeId: string) => void;
  /** R2 — ask the END USER for a report's render params via a guided dialog
   *  (fields from the Type provider's declared `param_schema`, pre-filled with
   *  the action's static values). Resolves the values to render with, or
   *  `null` when the user cancels (the print is aborted). Optional so a
   *  partial/preview runtime degrades to "render with what you have". */
  promptReportParams?: (opts: {
    title: string;
    schema: PortalReportParam[];
    initial: Record<string, unknown>;
    /** R2b — resolves a param's dynamic options (`options_from`); the dialog
     *  renders a dropdown when set, degrading to a text input on failure. */
    fetchOptions?: (param: string) => Promise<PortalReportParamOption[]>;
  }) => Promise<Record<string, unknown> | null>;
  /** Execution mode (ADR-0045 R3). The portal runtime leaves it unset (`live`);
   *  the BUILDER's running-canvas sandbox sets `"preview"` so `runActions`
   *  suppresses navigation + server actions while UI/state actions run. */
  mode?: ActionExecMode;
}

const Ctx = createContext<PageRuntime | null>(null);

/** Runtime if rendered inside a portal page; `null` in the builder preview. */
export function useOptionalRuntime(): PageRuntime | null {
  return useContext(Ctx);
}

export function PageRuntimeProvider({
  appSlug,
  result,
  refresh,
  initialVars,
  input,
  onClose,
  globalVariables,
  children,
}: {
  appSlug: string;
  result: PortalPageResult;
  /** Re-fetch the page (used by the `refresh` action + after form submit). */
  refresh: () => void;
  /** DECLARED app-wide `$global` variables (WS-4a, `apps.global_state`) — seeded
   *  into the app store on mount (only missing keys) so `{{ $global.<name> }}`
   *  resolves to its initial value in the builder canvas + the live portal. */
  globalVariables?: { name: string; initial?: unknown }[];
  /** Explicit seed vars (a drawer passes `{ row: <id> }`); merged over the
   *  AST defaults + URL query params. */
  initialVars?: Record<string, unknown>;
  /** Read-only `$input` namespace when this page is an overlay (ADR-0023). */
  input?: Record<string, unknown>;
  /** When this provider renders INSIDE an overlay, the overlay's close handler
   *  (optionally carrying a `result`) — exposed as `runtime.closeDrawer` /
   *  `runtime.closeOverlay` so a block can self-close + return a result. */
  onClose?: (result?: Record<string, unknown>) => void;
  children: ReactNode;
}) {
  const navigateRaw = useNavigate();
  // `navigate("back")` = browser history back, so a detail page opened from
  // different places (catalog vs management) returns to whichever one opened it.
  const go = (to: string) => (to === "back" ? navigateRaw(-1) : navigateRaw(to));
  // Inside an overlay, navigating closes this overlay first (then routes) — the
  // route change also unmounts the whole stack, so the dialog never lingers over
  // a freshly-navigated page. At top level it's a plain navigate.
  const navigate = onClose
    ? (to: string) => {
        onClose();
        go(to);
      }
    : go;
  const [search] = useSearchParams();
  const [vars, setVars] = useState<Record<string, unknown>>(() => {
    const v: Record<string, unknown> = {};
    for (const x of result.page.ast.variables ?? []) v[x.name] = x.initial ?? null;
    // URL query params override declared defaults — this is how a row action
    // ("?row=<id>") feeds the record block's `{{ $page.row }}` (S5).
    for (const [k, val] of search.entries()) v[k] = val;
    // Explicit seed (drawer) wins last.
    if (initialVars) for (const [k, val] of Object.entries(initialVars)) v[k] = val;
    return v;
  });
  // Reactive engine (ADR-0043 F1) — the dep-graph oracle for the page. It owns
  // the real dependency model: `setVar` writes into it and asks which queries a
  // change made `stale` (replacing the old per-resource `depSnapshot` heuristic).
  // Built once per mount; `$page` mirrors `vars`. React `vars` stays the render
  // state (the engine is the dep graph, not the renderer — see frontend/CLAUDE.md).
  const engineRef = useRef<ReactiveEngine | null>(null);
  if (engineRef.current === null) {
    const eng = createReactiveEngine(pageToCells(result.page.ast), {
      ...pageInitialState(result.page.ast),
      $page: vars,
    });
    eng.evaluateAll();
    engineRef.current = eng;
  }
  // App-scoped global store (ADR-0043). Lives at MODULE scope (outside this
  // per-page tree) keyed by app slug, so it survives navigation; subscribed via
  // `useSyncExternalStore` over a stable snapshot ref (replace-on-write).
  const store = useSyncExternalStore(
    subscribeStore,
    () => storeSnapshot(appSlug),
    () => storeSnapshot(appSlug),
  );
  // WS-4a — seed the DECLARED `$global` variables' initials into the app store
  // (idempotent: only keys not already written), so `{{ $global.<name> }}`
  // resolves in the builder canvas + the live portal. Missing-only keeps a value
  // the user wrote during the session across navigation; re-runs when a NEW
  // global is declared (the builder edits it live).
  useEffect(() => {
    if (globalVariables?.length) seedGlobals(appSlug, globalVariables);
  }, [appSlug, globalVariables]);
  const [toast, setToast] = useState<string | null>(null);
  // Overlay stack (ADR-0023): record drawers (`openDrawer`) + authored overlays
  // (`openOverlay`) share one stack, capped at MAX_OVERLAY_DEPTH.
  const [overlays, setOverlays] = useState<OverlayEntry[]>([]);
  const overlayId = useRef(0);
  // ADR-0045 — open modal/drawer COMPONENT nodes, keyed by node id (vs the
  // overlay-page stack above). Replace-on-write so the runtime value stays stable.
  const [openModalIds, setOpenModalIds] = useState<ReadonlySet<string>>(() => new Set());
  // R2 — a pending report-params request: the `print_report` action awaits the
  // dialog through the stored `resolve` (promise-bridged UI, like window.confirm
  // but portal-styled). One at a time — a print click is a discrete action.
  const [reportPrompt, setReportPrompt] = useState<{
    title: string;
    schema: PortalReportParam[];
    initial: Record<string, unknown>;
    fetchOptions?: (param: string) => Promise<PortalReportParamOption[]>;
    resolve: (v: Record<string, unknown> | null) => void;
  } | null>(null);
  // Dashboard-wide filters (3b): `filtered` holds the queries re-resolved with
  // the active global filters (null = no filter ⇒ fall back to the page-load
  // `result.queries`). A generation ref drops stale responses when filters
  // change faster than the network.
  const [globalFilters, setGlobalFilters] = useState<GlobalFilter[]>([]);
  const [filtered, setFiltered] = useState<Record<string, PortalRowsPage> | null>(null);
  const gen = useRef(0);

  const setGlobalFilter = (filters: GlobalFilter[]) => {
    setGlobalFilters(filters);
    const my = ++gen.current;
    if (filters.length === 0) {
      setFiltered(null);
      return;
    }
    const wire = filters.map((f) => ({ column: f.column, op: f.op, value: f.value }));
    const names = (result.page.ast.queries ?? []).map((q) => q.name);
    Promise.all(
      names.map((name) =>
        portalApi
          .queryPageData(appSlug, result.page.slug, name, { filters: wire })
          .then((res) => [name, res] as const)
          .catch(() => null),
      ),
    ).then((pairs) => {
      if (my !== gen.current) return; // a newer filter change superseded us
      const next: Record<string, PortalRowsPage> = { ...result.queries };
      for (const p of pairs) if (p) next[p[0]] = p[1];
      setFiltered(next);
    });
  };

  // Reactively re-resolve ONE resource (ADR-0022 Phase C). Merges the fresh
  // result into the live `filtered` overlay so only that `$queries.<name>`
  // changes — the rest of the page is untouched.
  const refreshResource: PageRuntime["refreshResource"] = async (name, params, opts) => {
    try {
      const res = await portalApi.resolveResource(appSlug, result.page.slug, name, params ?? {});
      setFiltered((prev) => ({ ...(prev ?? result.queries), [name]: res }));
    } catch (e) {
      // a failed re-resolve leaves the prior data in place (best-effort) — UNLESS
      // the imperative caller (the `run_query` action) asked to propagate so its
      // `try/catch` chain can react (ADR-0044 J3c).
      if (opts?.throwOnError) throw e;
    }
  };

  // Reactive dep-graph (ADR-0022 Phase C · slice 2). A resource that declares
  // `deps` (page-var names) auto-refetches when one of them changes: the client
  // re-evaluates its `{{ }}` params against the live scope and POSTs the
  // concrete values. Fires once on mount too (so a parameterised resource gets
  // correct initial data — the server page-load can't evaluate `$page`).
  const reactiveResources = useMemo(
    () => (result.page.ast.queries ?? []).filter(isReactive),
    [result.page.ast.queries],
  );
  // The names the reactive dep-graph OWNS — a `data_list` bound to one skips its
  // own server-side fetch (S16b) so it can't clobber the engine's `$page`-filtered
  // result (see PageRuntime.reactiveQueryNames).
  const reactiveQueryNames = useMemo(
    () => new Set(reactiveResources.map((r) => r.name)),
    [reactiveResources],
  );
  const currentQueries = filtered ?? result.queries;

  // Build the binding scope used to resolve a reactive query's `{{ }}` params.
  const reactiveScope = (pageVars: Record<string, unknown>): Scope => {
    const queryScope: Record<string, unknown> = {};
    for (const [name, p] of Object.entries(currentQueries)) {
      queryScope[name] = { rows: p.rows, total: p.total, table_id: p.table_id, data: p.data };
    }
    return { $user: result.user, $role: result.role, $queries: queryScope, $page: pageVars };
  };

  // Initial parameterised fetch (ADR-0022 Phase C): a reactive resource whose
  // deps are already non-empty at mount fetches once (the server page-load can't
  // evaluate `$page`). Subsequent refetches are driven by `setVar` via the engine.
  const didMountFetch = useRef(false);
  useEffect(() => {
    if (didMountFetch.current) return;
    didMountFetch.current = true;
    const scope = reactiveScope(vars);
    for (const spec of reactiveResources) {
      if (allDepsEmpty(spec, vars)) continue;
      void refreshResource(spec.name, computeRuntimeParams(spec, scope));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A write action (`create_row`/`update_row`/`delete_row`) + the `refresh`
  // action call this. Re-fetching the whole page (`refresh()`) refreshes the
  // static + serverMode lists (their fetch keys off `rt.queries[ref]`), but a
  // REACTIVE non-serverMode list (e.g. `versioni` filtered by `{{ $page.row }}`)
  // renders from the CLIENT-resolved `filtered[name]` — which the page refetch
  // can't update (the server can't evaluate `$page`). So also re-fire every
  // reactive resource with the live engine scope, or a row just created inside a
  // `$page`-scoped list won't appear until navigation (owner-reported bug).
  const refreshAll = () => {
    refresh();
    const pageVars = (engineRef.current?.scope.$page as Record<string, unknown>) ?? vars;
    const scope = reactiveScope(pageVars);
    for (const spec of reactiveResources) {
      if (allDepsEmpty(spec, pageVars)) continue;
      void refreshResource(spec.name, computeRuntimeParams(spec, scope));
    }
  };

  const runtime: PageRuntime = {
    appSlug,
    pageSlug: result.page.slug,
    user: result.user,
    role: result.role,
    queries: filtered ?? result.queries,
    reactiveQueryNames,
    // Evaluate a reactive query's `{{ $page }}` params against the LIVE scope so a
    // server-side `data_list` (S16b) can carry the concrete filter into its fetch.
    // Reads `$page` from the engine (updated synchronously in `setVar` before the
    // refetch) so it's fresh the moment a selection changes.
    reactiveParams: (name) => {
      const spec = reactiveResources.find((r) => r.name === name);
      if (!spec) return null;
      const pageVars = (engineRef.current?.scope.$page as Record<string, unknown>) ?? vars;
      return computeRuntimeParams(spec, reactiveScope(pageVars));
    },
    vars,
    setVar: (name, value) => {
      setVars((s) => ({ ...s, [name]: value }));
      // The reactive engine owns the dep graph: it tells us which queries this
      // change made `stale`, and we refetch just those (no per-resource diffing).
      const eng = engineRef.current;
      if (!eng) return;
      const { stale } = eng.set(["$page", name], value);
      if (!stale.length) return;
      const pageVars = eng.scope.$page as Record<string, unknown>;
      const scope = reactiveScope(pageVars);
      for (const id of stale) {
        const qname = id.startsWith("query:") ? id.slice("query:".length) : id;
        const spec = reactiveResources.find((r) => r.name === qname);
        if (!spec || allDepsEmpty(spec, pageVars)) continue;
        void refreshResource(spec.name, computeRuntimeParams(spec, scope));
      }
    },
    store,
    storeValue: (key, value) => storeValue(appSlug, key, value),
    removeValue: (key) => removeValue(appSlug, key),
    clearStore: () => clearStore(appSlug),
    globalFilters,
    setGlobalFilter,
    refresh: refreshAll,
    refreshResource,
    // `.run()` (ADR-0043 F3): look up the named query across ALL declared
    // queries (incl. `manual` ones, which `reactiveResources` filters out),
    // evaluate its params against the live `$page` from the engine, refetch it.
    runQuery: (name) => {
      const spec = (result.page.ast.queries ?? []).find((q) => q.name === name);
      if (!spec) return Promise.resolve();
      const pageVars = (engineRef.current?.scope.$page as Record<string, unknown>) ?? vars;
      // PROPAGATE: this is the imperative action path, so a failure must reach the
      // action chain's `try/catch` (it subsumes `run_operation`, ADR-0044 J3c).
      return refreshResource(spec.name, computeRuntimeParams(spec, reactiveScope(pageVars)), {
        throwOnError: true,
      });
    },
    navigate,
    toast: (message) => {
      setToast(message);
      window.setTimeout(() => setToast(null), 3000);
    },
    input: input ?? {},
    openDrawer: (pageSlug, dv) =>
      setOverlays((s) =>
        s.length >= MAX_OVERLAY_DEPTH
          ? s
          : [...s, { id: ++overlayId.current, pageSlug, mode: "drawer", vars: dv, input: {} }],
      ),
    openOverlay: ({ page, mode, input: ov, onClose: chain }) =>
      setOverlays((s) =>
        s.length >= MAX_OVERLAY_DEPTH
          ? s // depth cap — silently ignore (a runaway chain can't stack forever)
          : [
              ...s,
              {
                id: ++overlayId.current,
                pageSlug: page,
                mode,
                vars: {},
                input: ov,
                onCloseChain: chain,
              },
            ],
      ),
    closeDrawer: onClose ? () => onClose() : undefined,
    closeOverlay: onClose,
    isModalOpen: (id) => openModalIds.has(id),
    openModal: (id) => setOpenModalIds((s) => (s.has(id) ? s : new Set(s).add(id))),
    closeModal: (id) =>
      setOpenModalIds((s) => {
        if (!s.has(id)) return s;
        const n = new Set(s);
        n.delete(id);
        return n;
      }),
    promptReportParams: (opts) => new Promise((resolve) => setReportPrompt({ ...opts, resolve })),
  };

  return (
    <Ctx.Provider value={runtime}>
      {globalFilters.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm">
          <span className="text-muted-foreground">Filtri attivi:</span>
          {globalFilters.map((f, i) => (
            <span
              key={`${f.column}:${i}`}
              className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-accent"
            >
              {f.label ?? f.column}: {String(f.value)}
            </span>
          ))}
          <button
            type="button"
            onClick={() => setGlobalFilter([])}
            className="ml-auto rounded px-2 py-0.5 text-xs text-muted-foreground hover:bg-foreground/10 hover:text-foreground"
          >
            Azzera
          </button>
        </div>
      )}
      {children}
      {toast && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 rounded-md bg-foreground px-4 py-2 text-sm text-background shadow-lg">
          {toast}
        </div>
      )}
      {overlays.map((o) => (
        <PortalOverlay
          key={o.id}
          appSlug={appSlug}
          pageSlug={o.pageSlug}
          mode={o.mode}
          vars={o.vars}
          input={o.input}
          onClose={(out) => {
            setOverlays((s) => s.filter((x) => x.id !== o.id));
            // Run the opener's on_close chain with the returned `$result`, then
            // refresh the opener so an edit/create done inside a drawer/overlay is
            // reflected. Use `refreshAll` (NOT the bare page `refresh`): a REACTIVE
            // non-serverMode list (e.g. a `$page.row`-filtered `abbinamenti` list)
            // renders from the client-resolved `filtered[name]`, which a page
            // refetch can't update — so a row created in a drawer form only showed
            // after a manual reload. `refreshAll` re-fires the reactive resources.
            o.onCloseChain?.(out ?? {});
            refreshAll();
          }}
        />
      ))}
      {reportPrompt && (
        <ReportParamsDialog
          title={reportPrompt.title}
          schema={reportPrompt.schema}
          initial={reportPrompt.initial}
          fetchOptions={reportPrompt.fetchOptions}
          onDone={(v) => {
            reportPrompt.resolve(v);
            setReportPrompt(null);
          }}
        />
      )}
    </Ctx.Provider>
  );
}

/** Coerce a dialog value to the DECLARED param type — number/boolean only (the
 *  declaration is the contract, so this is not client type-guessing; a value
 *  that doesn't parse stays the raw string for the server to judge). */
function coerceReportParam(type: string, raw: unknown): unknown {
  if (type === "number" && typeof raw === "string" && raw.trim() !== "") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
  }
  return raw;
}

/** R2b — one param's dynamic options, fetched on mount. Degrades HONESTLY:
 *  while loading → disabled select; fetch failure or an empty source → the
 *  free-text input (never block the print on a broken source). */
function ParamOptionsSelect({
  param,
  value,
  onChange,
  fetchOptions,
}: {
  param: PortalReportParam;
  value: string;
  onChange: (v: string) => void;
  fetchOptions: (param: string) => Promise<PortalReportParamOption[]>;
}) {
  const [options, setOptions] = useState<PortalReportParamOption[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchOptions(param.name)
      .then((opts) => {
        if (!alive) return;
        if (opts.length === 0) setFailed(true);
        else setOptions(opts);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [param.name]);
  if (failed) {
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
      />
    );
  }
  return (
    <select
      value={value}
      disabled={options === null}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
    >
      <option value="">{options === null ? "Caricamento…" : "— Seleziona —"}</option>
      {(options ?? []).map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/** R2 — the end-user report-params dialog: one field per declared param
 *  (dropdown when the param declares an options source, checkbox for boolean,
 *  text otherwise), pre-filled with the action's static values; Stampa is
 *  gated on the required fields being filled. */
function ReportParamsDialog({
  title,
  schema,
  initial,
  fetchOptions,
  onDone,
}: {
  title: string;
  schema: PortalReportParam[];
  initial: Record<string, unknown>;
  fetchOptions?: (param: string) => Promise<PortalReportParamOption[]>;
  onDone: (values: Record<string, unknown> | null) => void;
}) {
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const v: Record<string, unknown> = {};
    for (const p of schema) {
      const seed = initial[p.name];
      if (p.type === "boolean") v[p.name] = seed === true || seed === "true";
      else v[p.name] = seed === undefined || seed === null ? "" : String(seed);
    }
    return v;
  });
  const missingRequired = schema.some(
    (p) => p.required && p.type !== "boolean" && String(values[p.name] ?? "").trim() === "",
  );
  const submit = () => {
    const out: Record<string, unknown> = { ...initial };
    for (const p of schema) {
      const raw = values[p.name];
      if (p.type !== "boolean" && String(raw ?? "").trim() === "") continue; // omitted
      out[p.name] = coerceReportParam(p.type, raw);
    }
    onDone(out);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4">
      <div className="w-full max-w-sm rounded-lg border border-border bg-background p-4 shadow-xl">
        <div className="mb-1 text-sm font-semibold">{title || "Stampa report"}</div>
        <div className="mb-3 text-xs text-muted-foreground">
          Inserisci i parametri per generare il report.
        </div>
        <div className="space-y-3">
          {schema.map((p) => (
            <label key={p.name} className="block text-sm">
              <span className="mb-1 block font-medium">
                {p.name}
                {p.required && <span className="text-danger"> *</span>}
              </span>
              {p.description && (
                <span className="mb-1 block text-xs text-muted-foreground">{p.description}</span>
              )}
              {p.type === "boolean" ? (
                <input
                  type="checkbox"
                  checked={values[p.name] === true}
                  onChange={(e) => setValues((s) => ({ ...s, [p.name]: e.target.checked }))}
                />
              ) : p.options_from && fetchOptions ? (
                <ParamOptionsSelect
                  param={p}
                  value={String(values[p.name] ?? "")}
                  onChange={(v) => setValues((s) => ({ ...s, [p.name]: v }))}
                  fetchOptions={fetchOptions}
                />
              ) : (
                <input
                  type="text"
                  inputMode={p.type === "number" ? "decimal" : undefined}
                  value={String(values[p.name] ?? "")}
                  onChange={(e) => setValues((s) => ({ ...s, [p.name]: e.target.value }))}
                  className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                />
              )}
            </label>
          ))}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onDone(null)}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-surface"
          >
            Annulla
          </button>
          <button
            type="button"
            disabled={missingRequired}
            onClick={submit}
            className="rounded-md bg-foreground px-3 py-1.5 text-sm font-medium text-background disabled:opacity-50"
          >
            Stampa
          </button>
        </div>
      </div>
    </div>
  );
}
